'use client';

import type { EditorState } from 'lexical';
import type { AppState, AppContextValue } from 'src/types/nya/appContextTypes';

import { useMemo, Suspense, useCallback, createContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useLocalStorage, getStorage, setStorage } from 'src/hooks/use-local-storage';

import { väljRäkenskapsår } from 'src/utils/räkenskapsår';

import { SplashScreen } from 'src/components/loading-screen';
import {IOrganisation, IRäkenskapsår} from "../../types/nya/common";
import { ALLA_ORG, TIDIGARE_PRIVATPERSON_ID, ärAllaId } from './alla-organisationer';

// ----------------------------------------------------------------------

/**
 * `useLayoutEffect` på klienten, `useEffect` vid SSR (där layouteffekter inte
 * körs och React annars varnar).
 */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppContextConsumer = AppContext.Consumer;

const STORAGE_KEY = 'app-context';

/**
 * Sätts när det redan sparade `aktuelltRäkenskapsår` har räknats om en gång.
 * Se läkningen i `Container` nedan.
 */
const MIGRERINGSNYCKEL_RÄKENSKAPSÅRSVAL = 'app-context:räkenskapsårsval-v1';

const initialState: AppState = {
  editorState: undefined,
  organisation: undefined,
  aktuelltRäkenskapsår: undefined,
  bankAccountPositions: {},
  minimeradeBankkonton: {},
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AppContextProvider({ children }: Props) {
  return <Container>{children}</Container>;
}

// ----------------------------------------------------------------------

function Container({ children }: Props) {
  const router = useRouter();

  const { state, setState, setField, canReset, resetState } = useLocalStorage<AppState>(
    STORAGE_KEY,
    initialState
  );

  const onUpdateEditorState = useCallback(
    (newEditorState: EditorState) => {
      setField('editorState', newEditorState);
    },
    [setField]
  );

  const uppdateraValdOrganisation = useCallback(
    (nyttOrganisationsVal: IOrganisation) => {
      setField('organisation', nyttOrganisationsVal);
      // Förvalt räkenskapsår vid orgbyte: det år som pågår nu. Listan får inte
      // indexeras på sista elementet — den kommer osorterad från API:t, och värdet
      // som skrivs här hamnar i localStorage och blir sanning för varje sida som
      // läser `aktuelltRäkenskapsår`.
      const valtRäkenskapsår = väljRäkenskapsår(nyttOrganisationsVal.räkenskapsår);
      if (valtRäkenskapsår) {
        setField('aktuelltRäkenskapsår', valtRäkenskapsår);
      }
    },
    [setField]
  );

  // Engångsläkning av redan sparade val. Tidigare valdes räkenskapsåret som listans
  // sista element, och eftersom listan inte kommer sorterad kunde det bli ett år
  // flera år tillbaka. Det värdet ligger kvar i localStorage tills organisationen
  // väljs om, så det räknas om en gång per webbläsare. Därefter rörs det aldrig
  // igen — årsväljarna i vyerna ska fortsätta äga användarens egna val.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (getStorage(MIGRERINGSNYCKEL_RÄKENSKAPSÅRSVAL)) return;

    const räkenskapsår = state.organisation?.räkenskapsår;
    // Vänta tills localStorage hunnit återställas; utan organisation finns inget att läka.
    if (!räkenskapsår?.length) return;

    const valt = väljRäkenskapsår(räkenskapsår);
    if (valt) {
      setField('aktuelltRäkenskapsår', valt);
    }
    setStorage(MIGRERINGSNYCKEL_RÄKENSKAPSÅRSVAL, 1);
  }, [state.organisation?.räkenskapsår, setField]);

  /**
   * Läget hette `privatperson` innan det blev `alla`. Sparade val ligger kvar i
   * localStorage tills något skriver om dem, och `src/lib/axios.ts` läser just
   * den strängen direkt ur lagringen för att avgöra om `X-Organisation-Id` ska
   * sättas. Ett kvarlämnat `privatperson` blir därför ett 400-svar på första
   * anropet — skrivningen måste ske, inte bara normaliseringen i minnet längre
   * ned.
   */
  useEffect(() => {
    if (state.organisation?.id !== TIDIGARE_PRIVATPERSON_ID) return;
    setField('organisation', ALLA_ORG);
  }, [state.organisation?.id, setField]);

  const uppdateraAktuelltRäkenskapsår = useCallback(
    (nyttRäkenskapsår: IRäkenskapsår) => {
      setField('aktuelltRäkenskapsår', nyttRäkenskapsår);
    },
    [setField]
  );

  // Nästan all cachad data hör till en organisation — SWR-nycklarna är URL:er och
  // organisationen följer med i `X-Organisation-Id`, inte i nyckeln. Vid byte måste
  // cachen därför tömmas, annars visar sidor kvar föregående förenings siffror.
  //
  // Tömningen sker efter att bytet skrivits till localStorage (så omhämtningarna
  // går iväg med rätt organisation) men före målningen, så att en omonterad sida
  // aldrig hinner visa fel förenings data en bildruta.
  const { mutate } = useSWRConfig();
  const föregåendeOrgId = useRef<string | undefined>(undefined);

  useIsomorphicLayoutEffect(() => {
    const orgId = state.organisation?.id;
    const föregående = föregåendeOrgId.current;
    föregåendeOrgId.current = orgId;

    // Första ifyllningen (localStorage återställs efter första renderingen) är inget byte.
    if (!föregående || föregående === orgId) {
      return;
    }

    mutate(() => true, undefined, { revalidate: true });
  }, [state.organisation?.id, mutate]);

  const onUpdateBankAccountPositions = useCallback(
    (orgId: string, positions: string[]) => {
      setField('bankAccountPositions', {
        ...state.bankAccountPositions,
        [orgId]: positions
      });
    },
    [setField, state.bankAccountPositions]
  );

  const onToggleMinimeraBankkonto = useCallback(
    (orgId: string, kontoId: string) => {
      const nuvarande = state.minimeradeBankkonton?.[orgId] || [];
      const uppdaterad = nuvarande.includes(kontoId)
        ? nuvarande.filter((id) => id !== kontoId)
        : [...nuvarande, kontoId];

      setField('minimeradeBankkonton', {
        ...(state.minimeradeBankkonton || {}),
        [orgId]: uppdaterad,
      });
    },
    [setField, state.minimeradeBankkonton]
  );

  // Reset
  const onReset = useCallback(() => {
    resetState();
    router.push(paths.dashboard.root);
  }, [ resetState, router]);

  // Alla organisationer är en attrapp utan rad i databasen — dess id är inte ens
  // ett UUID. Vyerna gatear sina hämtningar på `organisation`, så attrappen
  // lämnas ut som "ingen förening"; annars byggs org-scopade URL:er med `alla`
  // och varenda anrop svarar med fel. Själva valet finns kvar i
  // `valdOrganisation` för det som ska visa vad användaren valt.
  //
  // `vald` normaliseras här och inte bara i migreringseffekten nedan: effekten
  // kör efter första renderingen, och fram till dess hade ett sparat
  // `privatperson` räknats som en riktig förening.
  const ärAllaOrganisationer = ärAllaId(state.organisation?.id);
  const vald = ärAllaOrganisationer ? ALLA_ORG : state.organisation;
  const förening = ärAllaOrganisationer ? undefined : vald;

  const memoizedValue = useMemo(
    () => ({
      editorState: state.editorState,
      organisation: förening,
      valdOrganisation: vald,
      ärAllaOrganisationer,
      aktuelltRäkenskapsår: state.aktuelltRäkenskapsår,
      bankAccountPositions: state.bankAccountPositions,
      canReset,
      onReset,
      onUpdateEditorState,
      uppdateraValdOrganisation,
      uppdateraAktuelltRäkenskapsår,
      onUpdateBankAccountPositions,
      minimeradeBankkonton: state.minimeradeBankkonton || {},
      onToggleMinimeraBankkonto,
    }),
    [
      state.editorState,
      förening,
      vald,
      ärAllaOrganisationer,
      state.aktuelltRäkenskapsår,
      state.bankAccountPositions,
      canReset,
      onReset,
      onUpdateEditorState,
      uppdateraValdOrganisation,
      uppdateraAktuelltRäkenskapsår,
      onUpdateBankAccountPositions,
      state.minimeradeBankkonton,
      onToggleMinimeraBankkonto,
    ]
  );

  return <AppContext.Provider value={memoizedValue}>{children}</AppContext.Provider>;
}
